import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import {
  getInstallPrompt,
  isIosDevice,
  isIosSafari,
  isStandaloneApp,
  isPwaInstallPromptDismissed,
  dismissPwaInstallPrompt,
  subscribePwaInstallPrompt,
  subscribePwaInstallGuideRequest,
  promptInstall,
  subscribeInstallPrompt,
  type BeforeInstallPromptEvent,
} from '@/lib/pwa';

/**
 * 未ログインでも見た目が崩れないよう、ブランドカラー（ログイン画面・
 * PWA theme-color と同じ赤系）をローカルに固定して共通ボタンへ継承させる。
 * 各ページのテーマカラーはこのサブツリーには及ばないため、ここで明示する。
 */
const BRAND_THEME_VARS = {
  '--theme-primary': '#d92d20',
  '--theme-primary-hover': '#b42318',
  '--theme-primary-light': '#fef3f2',
} as CSSProperties;

/**
 * PWA としてインストールされていない（＝通常のブラウザ表示の）環境で、
 * アプリのインストールを促すグローバルモーダル。
 *
 * - インストール済みアプリ（standalone 等）では表示しない
 * - 会議中（招待URL・会議ハブ＝ `/meeting*`）では表示しない
 * - 一度閉じたら端末に記憶し、以後は表示しない（通知設定から再表示できる）
 * - 通知の「アプリ必須」導線（通知ベルなど）からは `requestPwaInstallGuide`
 *   経由で即時表示できる
 * - Android/Chrome は `beforeinstallprompt` からネイティブのインストールを起動
 * - iOS は「ホーム画面に追加」の手順を案内（Safari のみインストール可能）
 */
export default function PwaInstallPrompt() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(getInstallPrompt());
  const [installing, setInstalling] = useState(false);
  // 「今後表示しない」の記憶（端末単位・アプリ全体で共有）
  const [dismissed, setDismissed] = useState<boolean>(() => isPwaInstallPromptDismissed());
  // 通知設定のリセット直後は待たずに表示する
  const justResetRef = useRef(false);

  // ネイティブのインストール促しイベントの取得可否を購読する
  useEffect(() => subscribeInstallPrompt(setDeferred), []);

  // 「今後表示しない」の記憶の変化（インストール完了・リセット）を購読する
  useEffect(
    () =>
      subscribePwaInstallPrompt((next) => {
        if (!next) justResetRef.current = true;
        setDismissed(next);
      }),
    [],
  );

  // 通知の「アプリ必須」導線から開かれたら、その場で表示する
  useEffect(
    () =>
      subscribePwaInstallGuideRequest(() => {
        // 「今後表示しない」を記憶していても、明示操作なのでその場は表示する
        setDismissed(false);
        setVisible(true);
      }),
    [],
  );

  // 会議中（招待URL・会議ハブ）ではご案内を出さない
  const isMeetingRoute = location.pathname.startsWith('/meeting');

  // 表示可否の判定（インストール済み・記憶済み・会議中は出さない）
  useEffect(() => {
    if (dismissed || isStandaloneApp() || isMeetingRoute) {
      setVisible(false);
      return undefined;
    }
    if (visible) return undefined;

    const delay = justResetRef.current ? 0 : 1200;
    justResetRef.current = false;
    const timer = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timer);
  }, [dismissed, isMeetingRoute, visible]);

  // インストール完了（別経路でのインストールを含む）で閉じて記憶する
  useEffect(() => {
    const handleInstalled = () => {
      dismissPwaInstallPrompt();
      setVisible(false);
    };
    window.addEventListener('appinstalled', handleInstalled);
    return () => window.removeEventListener('appinstalled', handleInstalled);
  }, []);

  const dismiss = useCallback(() => {
    dismissPwaInstallPrompt();
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    setInstalling(true);
    try {
      const outcome = await promptInstall();
      // インストールを承諾したら少し待って閉じる（進行中の体験を妨げない）
      if (outcome === 'accepted') {
        window.setTimeout(dismiss, 800);
      }
    } finally {
      setInstalling(false);
    }
  }, [dismiss]);

  return (
    <div style={BRAND_THEME_VARS}>
      <Modal
        open={visible}
        onClose={dismiss}
        title="アプリとして使う"
        subtitle="ホーム画面に追加すると、アプリのように起動でき、通知も受け取れます"
        maxWidth="max-w-md"
        icon={
          <span className="w-10 h-10 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
            <i className="ri-smartphone-line text-xl"></i>
          </span>
        }
        footer={
          <Button variant="outline" size="sm" icon="ri-eye-off-line" onClick={dismiss}>
            今後表示しない
          </Button>
        }
      >
        <div className="space-y-3">
          {/* インストール案内 */}
          <div className="rounded-xl border border-gray-200 p-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
              <i className="ri-download-2-line text-lg"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">ホーム画面に追加</p>

              {deferred ? (
                <>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    インストールすると、次回からホーム画面のアイコンからすぐに起動できます。
                  </p>
                  <div className="mt-3">
                    <Button
                      size="sm"
                      icon="ri-download-2-line"
                      onClick={handleInstall}
                      disabled={installing}
                    >
                      {installing ? 'インストール中...' : 'アプリをインストール'}
                    </Button>
                  </div>
                </>
              ) : isIosSafari() ? (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    画面下の「共有」ボタンから追加できます。
                  </p>
                  <ol className="text-xs text-gray-700 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0 text-[10px] font-semibold">
                        1
                      </span>
                      <span>
                        画面下の<i className="ri-share-forward-line mx-0.5 align-middle"></i>
                        「共有」をタップ
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0 text-[10px] font-semibold">
                        2
                      </span>
                      <span>「ホーム画面に追加」を選択</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0 text-[10px] font-semibold">
                        3
                      </span>
                      <span>右上の「追加」をタップ</span>
                    </li>
                  </ol>
                </div>
              ) : isIosDevice() ? (
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  お使いのブラウザでは直接追加できません。Safari でこのページを開き、
                  「共有」→「ホーム画面に追加」を選んでください。
                </p>
              ) : (
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  ブラウザのメニュー（右上の「︙」）またはアドレスバーのインストールアイコンから
                  「アプリをインストール」を選んでください。
                </p>
              )}
            </div>
          </div>

          {/* 通知の案内（通知はインストール後に利用可能） */}
          <div className="rounded-xl border border-gray-200 p-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
              <i className="ri-notification-3-line text-lg"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">通知はアプリで使えます</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                お知らせやメッセージの通知は、アプリとしてインストールした端末でのみ受け取れます。
                上の手順でホーム画面に追加したあと、通知をオンにしてください。
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}